import React, { useState, useEffect } from "react";
import { Tabs, Dropdown, Menu, Table, Spin } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  AppstoreOutlined,
  UserOutlined,
  CalendarOutlined,
  BankOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjectDetail } from "../../redux/actions";
import ManageJobCard from "../ManageJobs/components/ManageJobCard";
import { getPictureUrl } from "../../shared/helpers";
import moment from "moment";
import "./ProjectDetail.css";

const { TabPane } = Tabs;

const AVATAR_COLORS = [
  "#002D40", "#0958d9", "#1890ff", "#722ed1", "#fa8c16",
  "#eb2f96", "#13c2c2", "#f5222d", "#52c41a", "#2f54eb",
];

const getAvatarColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name, email) => {
  if (name && !/^Account\s+\d+$/i.test(name)) {
    const parts = name.trim().split(" ");
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return (email || "?").slice(0, 2).toUpperCase();
};

const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "active":
      return { background: "#e6f7ff", color: "#096dd9", border: "1px solid #91d5ff" };
    case "draft":
      return { background: "#f5f5f5", color: "#595959", border: "1px solid #d9d9d9" };
    case "inactive":
      return { background: "#fff1f0", color: "#cf1322", border: "1px solid #ffa39e" };
    default:
      return { background: "#f0f5ff", color: "#2f54eb", border: "1px solid #adc6ff" };
  }
};

export default function ProjectDetail() {
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("overview");

  const { project, jobs, counts, talents, talentsCount, managers, managersCount, loading } = useSelector(
    (state) => state.projectDetailReducer
  );

  useEffect(() => {
    if (id) dispatch(fetchProjectDetail(id));
  }, [id]);

  const moreMenu = (
    <Menu>
      <Menu.Item
        key="edit"
        icon={<EditOutlined />}
        onClick={() => history.push(`/manage-projects/edit/${id}`)}
      >
        Edit Project
      </Menu.Item>
    </Menu>
  );

  const managerColumns = [
    {
      title: "Manager",
      key: "manager",
      render: (_, record) => {
        const imgSrc = record.pictureUrl ? getPictureUrl(record.pictureUrl) : null;
        return (
          <div className="pd-manager-name-cell">
            {imgSrc ? (
              <img
                src={imgSrc}
                alt=""
                className="pd-manager-avatar-img"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="pd-manager-avatar"
              style={{
                background: getAvatarColor(record.email || ""),
                display: imgSrc ? "none" : "flex",
              }}
            >
              {getInitials(record.name, record.email)}
            </div>
            <div>
              <div className="pd-manager-display-name">{record.name || record.email}</div>
              <div className="pd-talent-sub">{record.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      render: (v) => v || "—",
    },
  ];

  const talentColumns = [
    {
      title: "Talent",
      key: "talent",
      render: (_, record) => {
        const fullName = `${record.profileFirstName || ""} ${record.profileLastName || ""}`.trim();
        const imgSrc = record.profilePicture ? getPictureUrl(record.profilePicture) : null;
        return (
          <div className="pd-manager-name-cell">
            {imgSrc ? (
              <img
                src={imgSrc}
                alt=""
                className="pd-manager-avatar-img"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="pd-manager-avatar"
              style={{
                background: getAvatarColor(record.email || ""),
                display: imgSrc ? "none" : "flex",
              }}
            >
              {getInitials(fullName, record.email)}
            </div>
            <div>
              <div className="pd-manager-display-name">{fullName || record.email}</div>
              <div className="pd-talent-sub">{record.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Project",
      key: "project",
      render: (_, record) => (
        <div>
          <div className="pd-talent-main">{record.skillListingTitle || "—"}</div>
        </div>
      ),
    },
    {
      title: "Job Details",
      key: "jobDetails",
      render: (_, record) => {
        const title = record.gigTitle || "—";
        const policy = record.gigs?.WorkplacePolicy || record.workplacePolicy || "";
        const jobId = record.jobOrderId;
        return (
          <div>
            <div
              className={`pd-talent-main${jobId ? " pd-job-link" : ""}`}
              onClick={() => jobId && history.push(`/jobdetail/${jobId}`)}
            >
              {title}
            </div>
            {policy && <div className="pd-talent-sub">{policy}</div>}
          </div>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (v) => v || "—",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const statusColors = {
          new: { background: "#e6f7ff", color: "#096dd9", border: "1px solid #91d5ff" },
          accepted: { background: "#e6f7ff", color: "#096dd9", border: "1px solid #91d5ff" },
          rejected: { background: "#fff1f0", color: "#cf1322", border: "1px solid #ffa39e" },
          hired: { background: "#f9f0ff", color: "#531dab", border: "1px solid #d3adf7" },
        };
        const key = (record.bidStatus || "").toLowerCase();
        const style = statusColors[key] || { background: "#f5f5f5", color: "#595959", border: "1px solid #d9d9d9" };
        return (
          <span className="pd-status-badge" style={style}>
            {record.bidStatus || "—"}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const conversationId = record.conversationId;
        return (
          <button
            className="pd-action-msg-btn"
            disabled={!conversationId}
            style={!conversationId ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            onClick={() => conversationId && history.push(`/conversations/${conversationId}`)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="8.5" cy="10" r="1" fill="currentColor" />
              <circle cx="12" cy="10" r="1" fill="currentColor" />
              <circle cx="15.5" cy="10" r="1" fill="currentColor" />
            </svg>
          </button>
        )
      },
    },
  ];

  if (loading && !project) {
    return (
      <div className="pd-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="pd-page">
      <div className="pd-topbar">
        <button className="pd-back-btn" onClick={() => history.push("/manage-projects")}>
          <ArrowLeftOutlined />
        </button>
        <div className="pd-topbar-center">
          <div className="pd-title-row">
            <span className="pd-project-icon">
              <svg width="22" height="22" viewBox="0 0 34 34" fill="none">
                <path
                  d="M17 4L7 9V19C7 24.52 11.58 29.74 17 31.2C22.42 29.74 27 24.52 27 19V9L17 4Z"
                  fill="#1890ff" opacity="0.18"
                />
                <path
                  d="M17 4L7 9V19C7 24.52 11.58 29.74 17 31.2C22.42 29.74 27 24.52 27 19V9L17 4Z"
                  stroke="#1890ff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </span>
            <h2 className="pd-project-name">{project?.projectName || "—"}</h2>
            <span className="pd-status-badge" style={getStatusStyle(project?.status)}>
              {project?.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : "—"}
            </span>
          </div>
        </div>
        <div className="pd-topbar-actions">
          <button
            className="pd-icon-btn"
            onClick={() => history.push(`/manage-projects/edit/${id}`)}
          >
            <EditOutlined />
          </button>
          <Dropdown overlay={moreMenu} trigger={["click"]} placement="bottomRight">
            <button className="pd-icon-btn">
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="pd-tabs">
        <TabPane tab="Overview" key="overview">
          <div className="pd-overview">

            <div className="pd-desc-card">
              <div className="pd-desc-header">Project Description</div>
              <p className="pd-desc-text">{project?.projectDescription || "—"}</p>
              <div className="pd-meta-row">
                <div className="pd-meta-item">
                  <CalendarOutlined className="pd-meta-icon" />
                  <div>
                    <div className="pd-meta-label">Start Date</div>
                    <div className="pd-meta-value">
                      {project?.startDate ? moment(project.startDate).format("MMM DD, YYYY") : "—"}
                    </div>
                  </div>
                </div>
                <div className="pd-meta-item">
                  <CalendarOutlined className="pd-meta-icon" />
                  <div>
                    <div className="pd-meta-label">End Date</div>
                    <div className="pd-meta-value">
                      {project?.dueDate
                        ? moment(project.dueDate).format("MMM DD, YYYY")
                        : project?.endDate
                        ? moment(project.endDate).format("MMM DD, YYYY")
                        : "—"}
                    </div>
                  </div>
                </div>
                <div className="pd-meta-item">
                  <UserOutlined className="pd-meta-icon" />
                  <div>
                    <div className="pd-meta-label">Creator</div>
                    <div className="pd-meta-value">
                      {project?.createdBy?.fullName || "—"}
                    </div>
                  </div>
                </div>
                <div className="pd-meta-item">
                  <BankOutlined className="pd-meta-icon" />
                  <div>
                    <div className="pd-meta-label">Department</div>
                    <div className="pd-meta-value">
                      {project?.department?.departmentName || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pd-section-header">
              <span className="pd-section-title">
                Jobs ({counts?.jobsCount ?? (jobs || []).length})
              </span>
              <div className="pd-section-actions">
                {(counts?.jobsCount ?? (jobs || []).length) > 3 && (
                  <button
                    className="pd-view-all-btn"
                    onClick={() => history.push(`/manage-projects/${id}/jobs`)}
                  >
                    View All
                  </button>
                )}
                <button
                  className="pd-post-job-btn"
                  onClick={() => history.push("/postjob", { projectId: id })}
                >
                  <PlusOutlined /> Post a Job
                </button>
              </div>
            </div>

            {(jobs || []).length > 0 ? (
              <div className="pd-jobs-grid">
                {(jobs || []).slice(0, 3).map((job) => {
                  const loc = job.location;
                  const locationStr = typeof loc === "string"
                    ? loc
                    : loc?.LocationWithCountry || loc?.Location || loc?.LocationLong ||
                      [job.Cities?.CityName, job.Regions?.Code].filter(Boolean).join(", ") || "";
                  return (
                    <ManageJobCard
                      key={job.jobOrderId || job._id || job.id}
                      job={{
                        ...job,
                        id: job?.jobOrderId || job?.id || job?._id,
                        JobOrderId: job?.jobOrderId || job?.JobOrderId,
                        location: locationStr,
                        facilityname: job?.companyName || job?.companyname || job?.facilityname,
                        matchedbids: job?.matches ?? job?.counts?.matches ?? job?.matchedbids ?? 0,
                        placedbids: job?.bidsPlaced ?? job?.counts?.bidsPlaced ?? job?.placedbids ?? 0,
                        receivedbids: job?.bidsReceived ?? job?.counts?.bidsReceived ?? job?.receivedbids ?? 0,
                        candidates: job?.invited ?? job?.counts?.invited ?? job?.candidates ?? 0,
                        RegionCode: job?.location?.RegionCode || job?.RegionCode,
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="pd-talent-empty">
                <AppstoreOutlined className="pd-talent-icon" />
                <span>No jobs posted yet.</span>
              </div>
            )}

            <div className="pd-section-header" style={{ marginTop: 28 }}>
              <span className="pd-section-title">
                Talent ({talentsCount || (talents || []).length})
              </span>
            </div>

            {(talents || []).length > 0 ? (
              <Table
                className="pd-managers-table"
                columns={talentColumns}
                dataSource={(talents || []).map((t) => ({
                  ...t,
                  key: t.accountId,
                }))}
                pagination={false}
              />
            ) : (
              <div className="pd-talent-empty">
                <TeamOutlined className="pd-talent-icon" />
                <span>No talent assigned yet.</span>
              </div>
            )}

            <div className="pd-section-header" style={{ marginTop: 28 }}>
              <span className="pd-section-title">
                Managers ({managersCount || (managers || []).length})
              </span>
            </div>

            {(managers || []).length > 0 ? (
              <Table
                className="pd-managers-table"
                columns={managerColumns}
                dataSource={(managers || []).map((m) => ({
                  ...m,
                  key: m.accountId,
                }))}
                pagination={false}
              />
            ) : (
              <div className="pd-talent-empty">
                <UserOutlined className="pd-talent-icon" />
                <span>No managers assigned yet.</span>
              </div>
            )}

          </div>
        </TabPane>

      </Tabs>
    </div>
  );
}
